import React, { useState, useEffect, useCallback } from 'react';
import Footer from '../components/Footer';
import axiosInstance from '../utils/axios';
import {
  Table, Card, Input, Select, Button, Tag, Space, Badge,
  DatePicker, Tooltip, Row, Col, Statistic,
} from 'antd';
import {
  SearchOutlined, HomeOutlined, SafetyOutlined, ReloadOutlined,
  WarningOutlined, ClockCircleOutlined, UserOutlined, GlobalOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { hssColors, commonStyles } from '../config/theme';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Paraguay es UTC-4
const toParaguay = (isoStr) => {
  if (!isoStr) return '—';
  const d = new Date(isoStr + 'Z'); // UTC from server
  return d.toLocaleString('es-PY', { timeZone: 'America/Asuncion' });
};

const horaParaguay = (isoStr) => {
  if (!isoStr) return 0;
  const d = new Date(isoStr + 'Z');
  return parseInt(d.toLocaleString('es-PY', { timeZone: 'America/Asuncion', hour: '2-digit', hour12: false }));
};

const fueraHorario = (isoStr) => {
  const h = horaParaguay(isoStr);
  return h < 7 || h >= 20;
};

const statusColor = (code) => {
  if (!code) return 'default';
  if (code >= 500) return 'red';
  if (code >= 400) return 'orange';
  if (code >= 300) return 'blue';
  return 'green';
};

const metodoColor = { GET: 'blue', POST: 'green', PUT: 'orange', DELETE: 'red' };

const AuditLog = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total_hoy: 0, usuarios_activos_hoy: 0, errores_hoy: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);

  const [filtros, setFiltros] = useState({
    page: 1,
    per_page: 50,
    usuario_dni: null,
    metodo: null,
    buscar: '',
    fecha_desde: null,
    fecha_hasta: null,
  });

  const cargar = useCallback(async (f = filtros) => {
    setLoading(true);
    try {
      const params = { page: f.page, per_page: f.per_page };
      if (f.usuario_dni) params.usuario_dni = f.usuario_dni;
      if (f.metodo) params.metodo = f.metodo;
      if (f.buscar) params.buscar = f.buscar;
      if (f.fecha_desde) params.fecha_desde = f.fecha_desde;
      if (f.fecha_hasta) params.fecha_hasta = f.fecha_hasta;

      const r = await axiosInstance.get('/audit-log', { params });
      setLogs(r.data.logs);
      setTotal(r.data.total);
      setStats(r.data.stats);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    cargar();
    axiosInstance.get('/usuarios').then(r => setUsuarios(r.data)).catch(() => {});
  }, []);

  const aplicarFiltro = (cambio) => {
    const nuevo = { ...filtros, ...cambio, page: 1 };
    setFiltros(nuevo);
    cargar(nuevo);
  };

  const columns = [
    {
      title: 'Fecha / Hora',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 170,
      sorter: (a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0),
      render: (ts) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{toParaguay(ts)}</span>
          {fueraHorario(ts) && (
            <Tag color="purple" style={{ fontSize: 10 }}>
              <ClockCircleOutlined /> Fuera de horario
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Usuario',
      dataIndex: 'usuario_nombre',
      key: 'usuario',
      width: 150,
      sorter: (a, b) => (a.usuario_nombre || '').localeCompare(b.usuario_nombre || ''),
      render: (nombre, r) => (
        <Space size={6}>
          <UserOutlined style={{ color: hssColors.primary }} />
          <span style={{ fontWeight: 600 }}>{nombre || 'Anónimo'}</span>
        </Space>
      ),
    },
    {
      title: 'Acción',
      dataIndex: 'accion',
      key: 'accion',
      sorter: (a, b) => (a.accion || '').localeCompare(b.accion || ''),
      render: (accion) => <span style={{ fontSize: 13 }}>{accion}</span>,
    },
    {
      title: 'Método',
      dataIndex: 'metodo',
      key: 'metodo',
      width: 90,
      sorter: (a, b) => (a.metodo || '').localeCompare(b.metodo || ''),
      render: m => <Tag color={metodoColor[m] || 'default'} style={{ fontWeight: 700, fontFamily: 'monospace' }}>{m}</Tag>,
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      key: 'endpoint',
      sorter: (a, b) => (a.endpoint || '').localeCompare(b.endpoint || ''),
      render: ep => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: hssColors.textSecondary }}>
          {ep}
        </span>
      ),
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 130,
      render: ip => (
        <Space size={4}>
          <GlobalOutlined style={{ color: hssColors.gray400, fontSize: 12 }} />
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{ip || '—'}</span>
        </Space>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'status_code',
      key: 'status',
      width: 80,
      sorter: (a, b) => (a.status_code || 0) - (b.status_code || 0),
      render: code => (
        <Tag color={statusColor(code)} style={{ fontWeight: 700, fontFamily: 'monospace' }}>
          {code || '—'}
        </Tag>
      ),
    },
    {
      title: 'Alerta',
      key: 'alerta',
      width: 90,
      render: (_, r) => {
        if (r.status_code >= 500) return <Tag color="red"><WarningOutlined /> Error</Tag>;
        if (r.status_code >= 400) return <Tag color="orange"><WarningOutlined /> Fallo</Tag>;
        if (fueraHorario(r.timestamp)) return <Tag color="purple"><ClockCircleOutlined /> Nocturno</Tag>;
        return <span style={{ color: hssColors.gray400, fontSize: 12 }}>—</span>;
      },
    },
  ];

  const suspicious = logs.filter(l => l.status_code >= 400 || fueraHorario(l.timestamp)).length;

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .log-error td   { background-color: #fff1f0 !important; }
        .log-warning td { background-color: #fffbe6 !important; }
        .log-nocturno td { background-color: #f9f0ff !important; }
      `}</style>

      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <SafetyOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Registro de Actividad</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Auditoría y monitoreo de comportamiento de usuarios</p>
            </div>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => cargar()} loading={loading} style={{ borderRadius: 8 }}>Actualizar</Button>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
          </Space>
        </div>
      </div>

      <div style={{ padding: '24px 32px', flex: 1 }}>

        {/* Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[
            { title: 'Acciones hoy', value: stats.total_hoy, color: hssColors.primary, icon: <SafetyOutlined /> },
            { title: 'Usuarios activos hoy', value: stats.usuarios_activos_hoy, color: hssColors.success, icon: <UserOutlined /> },
            { title: 'Errores / Fallos hoy', value: stats.errores_hoy, color: hssColors.danger, icon: <WarningOutlined /> },
            { title: 'Alertas en vista actual', value: suspicious, color: hssColors.error, icon: <WarningOutlined /> },
          ].map(s => (
            <Col xs={12} lg={6} key={s.title}>
              <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, borderLeft: `4px solid ${s.color}` }}>
                <Statistic
                  title={<span style={{ color: hssColors.textSecondary, fontSize: 13 }}>{s.title}</span>}
                  value={s.value}
                  prefix={<span style={{ color: s.color }}>{s.icon}</span>}
                  valueStyle={{ color: s.color, fontWeight: 700 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filtros */}
        <Card style={{ marginBottom: 24, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}
          styles={{ body: { padding: '16px 20px' } }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={8} lg={6}>
              <Input
                placeholder="Buscar acción, endpoint, IP..."
                prefix={<SearchOutlined style={{ color: hssColors.gray400 }} />}
                style={{ borderRadius: 8 }}
                allowClear
                onChange={e => aplicarFiltro({ buscar: e.target.value })}
              />
            </Col>
            <Col xs={12} sm={6} lg={4}>
              <Select
                placeholder="Usuario" allowClear style={{ width: '100%', borderRadius: 8 }}
                onChange={v => aplicarFiltro({ usuario_dni: v || null })}
              >
                {usuarios.map(u => (
                  <Option key={u.dni} value={u.dni}>{u.nombre}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} sm={4} lg={3}>
              <Select
                placeholder="Método" allowClear style={{ width: '100%' }}
                onChange={v => aplicarFiltro({ metodo: v || null })}
              >
                {['GET', 'POST', 'PUT', 'DELETE'].map(m => (
                  <Option key={m} value={m}><Tag color={metodoColor[m]}>{m}</Tag></Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={10} lg={8}>
              <RangePicker
                style={{ width: '100%', borderRadius: 8 }}
                placeholder={['Desde', 'Hasta']}
                onChange={(_, strs) => aplicarFiltro({
                  fecha_desde: strs[0] ? strs[0] + 'T00:00:00' : null,
                  fecha_hasta: strs[1] ? strs[1] + 'T23:59:59' : null,
                })}
              />
            </Col>
            <Col xs={24} sm={4} lg={3}>
              <Badge count={total} overflowCount={99999} style={{ background: hssColors.primary }}>
                <span style={{ fontSize: 13, color: hssColors.textSecondary }}>registros totales</span>
              </Badge>
            </Col>
          </Row>
        </Card>

        {/* Tabla */}
        <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}
          styles={{ body: { padding: 0 } }}>
          <Table
            columns={columns}
            dataSource={logs}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 'max-content' }}
            style={{ borderRadius: 12, overflow: 'hidden' }}
            rowClassName={(r) => {
              if (r.status_code >= 500) return 'log-error';
              if (r.status_code >= 400) return 'log-warning';
              if (fueraHorario(r.timestamp)) return 'log-nocturno';
              return '';
            }}
            pagination={{
              current: filtros.page,
              pageSize: filtros.per_page,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
              showTotal: t => `Total: ${t} registros`,
              onChange: (p, size) => {
                const nuevo = { ...filtros, page: p, per_page: size };
                setFiltros(nuevo);
                cargar(nuevo);
              },
            }}
          />
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default AuditLog;
